import React, { useState, useEffect } from 'react';
import SearchFilterBar from '../SearchFilterBar';
import useImageError from '../../hooks/useImageError';
import Modal2 from '../Modal';
import {
  FilePreviewContainer,
  ImagePreview,
  PreviewContainer,
  PreviewList,
} from '../../containers/Collection/dragdrop-styles.jsx';

const TokenPropsModal = ({ show, handleClose, modalTitle, content }: {
  show?: boolean;
  handleClose?: () => void;
  modalTitle?: any;
  content?: any;
}) => (
  <Modal2
    show={!!show}
    handleClose={handleClose}
    modalTitle={modalTitle}
    content={content}
  />
);

// Rarity tier → badge styling for the token thumbnail overlay. `rarityTier`
// comes from the token's ipfs_metadata row (bronze|silver|gold|platinum|diamond).
// We only show a badge for tiers ABOVE bronze, per product spec.
const TIER_BADGE_STYLES: Record<string, { color: string; bg: string }> = {
  silver: { color: '#0a1428', bg: '#c0c0c0' },
  gold: { color: '#0a1428', bg: '#ffd700' },
  platinum: { color: '#0a1428', bg: '#e5e4e2' },
  diamond: { color: '#0a1428', bg: '#b9f2ff' },
};

function NFTPreviewGrid({
  files = [],
  onItemSelect,
  failedImages = new Set(),
  onImageError,
  onPaginate,
  currentPosition = 0,
  searchPlaceholder = "Instant Search",
  searchValue,
  onSearchChange,
  pagination,
  className = ''
}) {
  const [localFailedImages, setLocalFailedImages] = useState(failedImages);
  const { onError: useImageErrorHandler } = useImageError('nft');
  const [propsItem, setPropsItem] = useState<any>(null);

  useEffect(() => {
    setLocalFailedImages(failedImages);
  }, [failedImages]);

  const handleImageError = (index, imgurl, e) => {
    const item = (files as any[])[index];
    const stableId = `tok-${item?.tokId}`;
    if (!localFailedImages.has(stableId)) {
      const newFailed = new Set(localFailedImages);
      newFailed.add(stableId);
      setLocalFailedImages(newFailed);
      if (onImageError) onImageError(stableId, item);
    }
    useImageErrorHandler(e);
    if (e.target.onError) e.target.onError = null;
  };

  const handleCtrlClick = (file: any) => (e: React.MouseEvent) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      e.stopPropagation();
      setPropsItem(file);
    }
  };

  const renderFilePreview = (file, index) => {
    const stableId = `tok-${file.tokId}`;
    const hasFailed = localFailedImages.has(stableId);
    const imgErrorHandler = !hasFailed ? (e) => handleImageError(index, file.imgurl, e) : null;

    // A token with no resolvable image (metadata not fetched yet) still renders
    // a card so it isn't invisible on the collection page — draw the animated
    // shimmer placeholder (same pattern as SecCollectionList/CollectionListItem)
    // and keep the card clickable to open the detail modal.
    const hasImage = !!file.imgurl && !!file.meta?.image;

    return (
      <PreviewContainer key={stableId}>
        <div>
          {hasImage ? (
            <ImagePreview
              onClick={(e) => {
                handleCtrlClick(file)(e);
                if (!e.defaultPrevented) onItemSelect && onItemSelect(file, e.currentTarget.src);
              }}
              id={stableId}
              src={file.imgurl + "?" + btoa(file?.meta?.image)}
              alt={`NFT preview ${index}`}
              onError={imgErrorHandler}
            />
          ) : (
            <div
              className="pixel-collection-placeholder is-delayed"
              onClick={(e) => {
                handleCtrlClick(file)(e);
                if (!e.defaultPrevented) onItemSelect && onItemSelect(file, null);
              }}
              title={`Token #${file.tokId}`}
              aria-hidden={false}
            />
          )}
          {file?.aid > 0 && (
            <div className="nft-price-badge">
              <span>{file?.price}</span>
            </div>
          )}
          {/* Rarity tier badge — only for tiers above bronze. Overlaid top-right
              so it doesn't collide with the "for sale" banner (top full-width). */}
          {TIER_BADGE_STYLES[file?.rarityTier] && (
            <div
              style={{
                position: 'absolute',
                top: 6,
                right: 6,
                zIndex: 3,
                color: TIER_BADGE_STYLES[file.rarityTier].color,
                background: TIER_BADGE_STYLES[file.rarityTier].bg,
                borderRadius: 3,
                padding: '1px 6px',
                fontSize: 9,
                fontWeight: 800,
                letterSpacing: '0.5px',
                lineHeight: '14px',
                textTransform: 'uppercase',
                pointerEvents: 'none',
              }}
            >
              {file.rarityTier}
            </div>
          )}
          {/* "For Sale" banner — a token with a live auctionID is listed for
              sale. Drawn over the image/placeholder (the wrapping div is
              position:relative) so the card reads as for sale at a glance. */}
          {file?.aid > 0 && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              zIndex: 2,
              background: 'linear-gradient(90deg, rgba(28,120,255,.92), rgba(28,120,255,.55))',
              color: '#fff',
              fontSize: 10,
              fontWeight: 700,
              lineHeight: '16px',
              textAlign: 'center',
              textTransform: 'uppercase',
              letterSpacing: '.5px',
              padding: '1px 0',
            }}>
              for sale
            </div>
          )}
        </div>
      </PreviewContainer>
    );
  };

  const limit = pagination?.limit || 40;
  const total = pagination?.total ?? files.length;
  const currentStart = currentPosition ?? 0;
  const hasPrev = currentStart > 0;
  const hasNext = pagination?.hasNext ?? currentStart + files.length < total;
  const countText = files.length > 0
    ? `Showing ${currentStart + 1}–${currentStart + files.length} of ${total}`
    : 'No items';

  return (
    <div className={`pixel-card ${className}`}>
      <SearchFilterBar
        searchPlaceholder={searchPlaceholder}
        searchValue={searchValue}
        onSearchChange={onSearchChange}
        showFilter={false}
      />
      <FilePreviewContainer>
        <PreviewList id="pvList">
          {files.map((file, index) => renderFilePreview(file, index))}
        </PreviewList>
      </FilePreviewContainer>

      {files.length > 0 && (
        <div className="d-flex align-items-center justify-content-between mt-2">
          <button className="pixel-btn" disabled={!hasPrev} onClick={() => onPaginate?.('prev')}>Prev</button>
          <span className="text-secondary small">{countText}</span>
          <button className="pixel-btn" disabled={!hasNext} onClick={() => onPaginate?.('next')}>Next</button>
        </div>
      )}

      <TokenPropsModal
        show={!!propsItem}
        handleClose={() => setPropsItem(null)}
        modalTitle={propsItem ? `Token Props — #${propsItem.tokId ?? propsItem.id ?? ''}` : 'Token Props'}
        content={
          propsItem ? (
            <div style={{ maxHeight: '70vh', overflow: 'auto' }}>
              {Object.entries(propsItem).map(([key, value]) => (
                <div key={key} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                  <span style={{ color: '#aeb6c8', wordBreak: 'break-word' }}>{key}</span>
                  <span style={{ color: '#edf2ff', wordBreak: 'break-word', textAlign: 'right' }}>{typeof value === 'object' ? JSON.stringify(value) : String(value ?? '')}</span>
                </div>
              ))}
            </div>
          ) : null
        }
      />
    </div>
  );
}

export default NFTPreviewGrid;
